import LabelAndTextInput from '@components/inputs/LabelAndTextInput'
import SelectBoxAndLabel from '@components/select/SelectBoxAndLabel'
import Title from '@components/title/Title'
import { requiredValidator } from '@/utils/validators'
import type { DynamicFieldDefinition } from '../formDefinition'

import './Section.css'

interface DynamicFieldsProps {
	fields: DynamicFieldDefinition[]
	values: Record<string, string>
	onChange: (key: string, value: string) => void
}

function chunkFields(fields: DynamicFieldDefinition[], size: number): DynamicFieldDefinition[][] {
	const chunks: DynamicFieldDefinition[][] = []
	for (let index = 0; index < fields.length; index += size) {
		chunks.push(fields.slice(index, index + size))
	}
	return chunks
}

function getSelectOptions(field: DynamicFieldDefinition): { [key: string]: string } {
	if (field.type === 'boolean') {
		return {
			'': 'Selectionner',
			true: 'Oui',
			false: 'Non',
		}
	}

	const options: { [key: string]: string } = { '': 'Selectionner' }
	for (const option of field.options) {
		options[option] = option
	}
	return options
}

function getFieldsBySection(fields: DynamicFieldDefinition[]): [string, DynamicFieldDefinition[]][] {
	const grouped = new Map<string, DynamicFieldDefinition[]>()

	for (const field of fields) {
		const sectionName = field.section || 'Informations complementaires'
		const existing = grouped.get(sectionName) ?? []
		existing.push(field)
		grouped.set(sectionName, existing)
	}

	return Array.from(grouped.entries())
}

function DynamicFields(props: DynamicFieldsProps) {
	if (props.fields.length === 0) {
		return null
	}

	return (
		<>
			{getFieldsBySection(props.fields).map(([sectionName, sectionFields]) => (
				<div className='section' key={sectionName}>
					<Title>{sectionName}</Title>
					<div className='formContent'>
						{chunkFields(sectionFields, 2).map((row, rowIndex) => (
							<div className='fieldLine' key={`dynamic-${sectionName}-${rowIndex}`}>
								{row.map(field => {
									const validator = field.required
										? (input: string) => [requiredValidator(input)]
										: undefined

									if (field.type === 'select' || field.type === 'boolean') {
										return (
											<SelectBoxAndLabel
												key={field.key}
												id={`dynamic-${field.key}`}
												text={field.label}
												options={getSelectOptions(field)}
												value={props.values[field.key] ?? ''}
												validator={validator}
												onChange={value => props.onChange(field.key, value)}
											/>
										)
									}

									return (
										<LabelAndTextInput
											key={field.key}
											id={`dynamic-${field.key}`}
											label={field.label}
											type={field.type === 'number' ? 'number' : field.type}
											value={props.values[field.key] ?? ''}
											validator={validator}
											onChange={value => props.onChange(field.key, value)}
										/>
									)
								})}
							</div>
						))}
					</div>
				</div>
			))}
		</>
	)
}

export default DynamicFields
